/**
 * Date utilities for consistent timezone handling
 * All dates are displayed in Europe/Prague timezone
 */
import { format as formatDateFns, formatDistanceToNow as formatDistanceFns, isToday, isTomorrow, isPast } from 'date-fns';
import { toZonedTime, format as formatTz } from 'date-fns-tz';
import { cs } from 'date-fns/locale';

const PRAGUE_TIMEZONE = 'Europe/Prague';

/**
 * Converts a date string or Date object to Prague timezone
 * @param {string|Date} date - UTC date string or Date object
 * @returns {Date} Date object in Prague timezone
 */
export const toPragueTime = (date) => {
  if (!date) return null;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return toZonedTime(dateObj, PRAGUE_TIMEZONE);
};

/**
 * Formats a date in Prague timezone with the given format
 * @param {string|Date} date - UTC date string or Date object
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted date string
 */
export const formatInPrague = (date, formatStr = 'dd.MM.yyyy HH:mm:ss') => {
  if (!date) return '-';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatTz(dateObj, formatStr, { 
      timeZone: PRAGUE_TIMEZONE,
      locale: cs 
    });
  } catch {
    return String(date);
  }
};

/**
 * Formats a date for display (handles today/tomorrow/other)
 * @param {string|Date} date - UTC date string or Date object
 * @returns {string} Formatted date string
 */
export const formatDateDisplay = (date) => {
  if (!date) return '';
  try {
    const pragueDate = toPragueTime(date);
    if (isToday(pragueDate)) return 'Dnes';
    if (isTomorrow(pragueDate)) return 'Zítra';
    return formatTz(pragueDate, 'd. MMMM', { 
      timeZone: PRAGUE_TIMEZONE,
      locale: cs 
    });
  } catch {
    return String(date);
  }
};

/**
 * Formats a date as relative time (e.g., "před 2 hodinami")
 * @param {string|Date} date - UTC date string or Date object
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  if (!date) return '';
  try {
    const pragueDate = toPragueTime(date);
    return formatDistanceFns(pragueDate, { addSuffix: true, locale: cs });
  } catch {
    return String(date);
  }
};

/**
 * Gets the current date/time in Prague timezone for display
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted current date
 */
export const getCurrentPragueTime = (formatStr = "EEEE, d. MMMM yyyy") => {
  const now = new Date();
  return formatTz(now, formatStr, { 
    timeZone: PRAGUE_TIMEZONE,
    locale: cs 
  });
};

/**
 * Checks if a date is past (in Prague timezone)
 * @param {string|Date} date - UTC date string or Date object
 * @returns {boolean} True if date is in the past
 */
export const isPastInPrague = (date) => {
  if (!date) return false;
  const pragueDate = toPragueTime(date);
  return isPast(pragueDate);
};

/**
 * Checks if a date is today (in Prague timezone)
 * @param {string|Date} date - UTC date string or Date object
 * @returns {boolean} True if date is today
 */
export const isTodayInPrague = (date) => {
  if (!date) return false;
  const pragueDate = toPragueTime(date);
  return isToday(pragueDate);
};

/**
 * Checks if a date is tomorrow (in Prague timezone)
 * @param {string|Date} date - UTC date string or Date object
 * @returns {boolean} True if date is tomorrow
 */
export const isTomorrowInPrague = (date) => {
  if (!date) return false;
  const pragueDate = toPragueTime(date);
  return isTomorrow(pragueDate);
};
